/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import type { TableStatus } from "@lemmonhq/types";
import { formatPrice, sortTables } from "@lemmonhq/orders";
import { cn, useLocalization } from "@lemmonhq/uikit";
import {
  TABLE_CREDIT_LOW_BALANCE,
  tableCreditOf,
} from "@app/vue/components/TableCredit/tableCredit";
import { GiftCardSection } from "./Section";

export type GiftCardTablePickerProps = {
  tables: TableStatus[];
  value: number | null;
  currency: string;
  /** Show only the chosen table, e.g. a top-up started from that table. */
  locked?: boolean;
  onChange: (tableId: number) => void;
};

type Filter = "all" | "free" | "busy" | "credit";

/** Search and filters kick in once the floor is too big to scan at a glance. */
const TOOLS_FROM = 9;

const hasCredit = (ts: TableStatus) => !!tableCreditOf(ts)?.credited;

/**
 * Which table a table credit is sold to: a grid of tables with their state
 * and the credit they already have.
 */
export const GiftCardTablePicker = defineComponent<GiftCardTablePickerProps>({
  name: "GiftCardTablePicker",
  props: ["tables", "value", "currency", "locked", "onChange"],
  setup(props) {
    const { t } = useLocalization();
    const query = ref("");
    // Credit is mostly sold to arriving guests, so start on the free tables.
    const filter = ref<Filter>("free");

    const matches: Record<Filter, (ts: TableStatus) => boolean> = {
      all: () => true,
      free: (ts) => !ts.table.is_busy,
      busy: (ts) => !!ts.table.is_busy,
      credit: hasCredit,
    };

    const filters = computed(() =>
      (
        [
          ["free", t("pos.gift_card.table_free")],
          ["all", t("pos.gift_card.filter_all")],
          ["busy", t("general.words.busy")],
          ["credit", t("pos.gift_card.filter_credit")],
        ] as [Filter, string][]
      ).map(([key, label]) => ({
        key,
        label,
        count: props.tables.filter(matches[key]).length,
      })),
    );

    const visible = computed(() => {
      const sorted = sortTables(props.tables);
      if (props.locked) return sorted.filter((ts) => ts.id === props.value);
      const q = query.value.trim().toLowerCase();
      return sorted.filter(
        (ts) =>
          matches[filter.value](ts) &&
          (!q ||
            ts.table.name.toLowerCase().includes(q) ||
            (ts.party?.name || "").toLowerCase().includes(q)),
      );
    });

    const renderTile = (ts: TableStatus) => {
      const selected = ts.id === props.value;
      const credit = tableCreditOf(ts);
      const busy = !!ts.table.is_busy;
      return (
        <button
          key={ts.id}
          type="button"
          aria-pressed={selected}
          disabled={props.locked}
          onClick={() => props.onChange(ts.id)}
          title={
            busy ? t("general.words.busy") : t("pos.gift_card.table_free")
          }
          class={cn(
            "flex min-h-11 items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-all active:scale-[0.98]",
            selected
              ? "border-primary bg-primary/10 ring-2 ring-primary/40"
              : "border-base-300 bg-base-200 hover:border-primary/40 hover:bg-base-300",
            props.locked && "cursor-default active:scale-100",
          )}
        >
          {selected ? (
            <span class="material-symbols-outlined shrink-0 text-base text-primary">
              check_circle
            </span>
          ) : (
            <span
              class={cn(
                "mx-1 h-2 w-2 shrink-0 rounded-full",
                busy ? "bg-warning" : "bg-success",
              )}
            />
          )}
          <span class="min-w-0 flex-1 leading-tight">
            <span class="block truncate text-sm font-semibold text-accent">
              {ts.table.name}
            </span>
            {ts.party?.name ? (
              <span class="block truncate text-xs text-base-content/60">
                {ts.party.name}
              </span>
            ) : null}
          </span>
          {credit?.credited ? (
            <span
              class={cn(
                "shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums",
                credit.balance <= 0
                  ? "bg-error/15 text-error"
                  : credit.balance < TABLE_CREDIT_LOW_BALANCE
                    ? "bg-warning/15 text-warning"
                    : "bg-success/15 text-success",
              )}
            >
              {formatPrice(credit.balance, true, 0, props.currency)}
            </span>
          ) : null}
        </button>
      );
    };

    return () => {
      const showTools = !props.locked && props.tables.length >= TOOLS_FROM;
      return (
        <GiftCardSection
          icon="table_restaurant"
          title={t("pos.table_credit.pick_table")}
        >
          {showTools ? (
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label class="input input-bordered input-sm flex h-10 items-center gap-2 rounded-xl sm:w-48">
                <span class="material-symbols-outlined text-base text-base-content/50">
                  search
                </span>
                <input
                  type="search"
                  class="w-full grow"
                  placeholder={t("pos.gift_card.search_tables")}
                  value={query.value}
                  onInput={(e) =>
                    (query.value = (e.target as HTMLInputElement).value)
                  }
                />
              </label>
              <div class="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-0.5">
                {filters.value.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    aria-pressed={filter.value === f.key}
                    onClick={() => (filter.value = f.key)}
                    class={cn(
                      "flex h-10 shrink-0 items-center gap-1.5 rounded-full border px-3 text-sm transition-colors",
                      filter.value === f.key
                        ? "border-primary bg-primary text-primary-content"
                        : "border-base-300 bg-base-200 hover:bg-base-300",
                    )}
                  >
                    {f.label}
                    <span
                      class={cn(
                        "rounded-full px-1.5 text-xs tabular-nums",
                        filter.value === f.key
                          ? "bg-primary-content/20"
                          : "bg-base-300 text-base-content/60",
                      )}
                    >
                      {f.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {visible.value.length === 0 ? (
            <div class="flex flex-col items-center gap-1 rounded-xl border border-dashed border-base-300 p-6 text-center text-sm text-base-content/60">
              <span class="material-symbols-outlined text-2xl">
                search_off
              </span>
              {t("pos.gift_card.no_tables")}
            </div>
          ) : (
            <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {visible.value.map(renderTile)}
            </div>
          )}
        </GiftCardSection>
      );
    };
  },
});
